import { create } from "zustand";
import { Cart, CartItem, BookingFormData, User } from "../types";

interface CartState extends Cart {
  discount: number;
  discountReason: string;
  couponId: string | null;
  couponCode: string | null;
  // Actions
  addItem: (item: Omit<CartItem, "id">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  setBookingData: (data: Partial<BookingFormData>) => void;
  calculateTotals: (taxRate: number, user: User | null) => void;
  applyCoupon: (couponId: string, code: string, discountAmount: number, reason: string) => void;
  removeCoupon: () => void;
  clearCart: () => void;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  subtotal: 0,
  discount: 0,
  discountReason: "",
  couponId: null,
  couponCode: null,
  tax: 0,
  total: 0,
  bookingData: undefined,

  addItem: (item) => {
    const newItem: CartItem = {
      ...item,
      id: `${item.type}-${item.name}-${Date.now()}`,
    };

    set((state) => {
      const existingItem = state.items.find((i) => i.name === item.name && i.type === item.type);

      if (existingItem) {
        return {
          items: state.items.map((i) =>
            i.id === existingItem.id ? { ...i, quantity: i.quantity + item.quantity } : i
          ),
        };
      }

      return {
        items: [...state.items, newItem],
      };
    });

    // Recalculate totals after adding item
    const state = get();
    if (state.bookingData?.serviceId) {
      get().calculateTotals(0, null);
    }
  },

  removeItem: (id) => {
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
    }));

    get().calculateTotals(0, null);
  },

  updateQuantity: (id, quantity) => {
    if (quantity <= 0) {
      get().removeItem(id);
      return;
    }

    set((state) => ({
      items: state.items.map((item) => (item.id === id ? { ...item, quantity } : item)),
    }));

    get().calculateTotals(0, null);
  },

  setBookingData: (data) => {
    set((state) => ({
      bookingData: { ...state.bookingData, ...data },
    }));
  },

  calculateTotals: (_taxRate, _user) => {
    const state = get();
    const subtotal = state.items.reduce((sum, item) => sum + item.price * item.quantity, 0);

    // Keep existing coupon discount (recalculation happens when coupon is applied)
    const discount = state.couponId ? state.discount : 0;
    const discountReason = state.couponId ? state.discountReason : "";

    const discountedSubtotal = Math.max(subtotal - discount, 0);
    const tax = 0;
    const total = discountedSubtotal;

    set({ subtotal, discount, discountReason, tax, total });
  },

  applyCoupon: (couponId, code, discountAmount, reason) => {
    const state = get();
    const discountedSubtotal = Math.max(state.subtotal - discountAmount, 0);

    set({
      couponId,
      couponCode: code.toUpperCase(),
      discount: discountAmount,
      discountReason: reason,
      total: discountedSubtotal,
    });
  },

  removeCoupon: () => {
    const state = get();
    set({
      couponId: null,
      couponCode: null,
      discount: 0,
      discountReason: "",
      total: state.subtotal,
    });
  },

  clearCart: () => {
    set({
      items: [],
      subtotal: 0,
      discount: 0,
      discountReason: "",
      couponId: null,
      couponCode: null,
      tax: 0,
      total: 0,
      bookingData: undefined,
    });
  },
}));
